import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SecurityIcon from '@mui/icons-material/Security'
import GavelIcon from '@mui/icons-material/Gavel'
import { RegistrationConfig } from '../types/EnvironmentConfig.types'
import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigToggle from '../components/ConfigToggle'
import ConfigSelect from '../components/ConfigSelect'
import { environmentConfigSectionDividerSx, environmentConfigSectionRootSx } from '../utils/environmentConfigChrome'
import { DEFAULT_SIGNUP_WAITLIST_URL, waitlistUrlInputError } from '../../../../../../utils/signupWaitlist'

interface RegistrationSettingsProps {
  config: RegistrationConfig
  onChange: (updates: Partial<RegistrationConfig>) => void
  disabled?: boolean
  /** False when the loaded settings didn't include the stored blocked list (public read). */
  blockedDomainsShown?: boolean
}

const BLOCKED_DOMAINS_HELP = 'Comma-separated list of globally blocked email domains'
const BLOCKED_DOMAINS_NOT_SHOWN_HELP =
  "The saved list isn't shown here. Leave this empty to keep it - domains you enter " +
  'replace it when you save.'

/**
 * Registration Settings Section
 * Manages user registration, verification, and access control
 */
const RegistrationSettings: React.FC<RegistrationSettingsProps> = ({
  config,
  onChange,
  disabled = false,
  blockedDomainsShown = true,
}) => {
  const theme = useTheme()
  const [blockedDomainsInput, setBlockedDomainsInput] = useState(config.emailDomains.blocked.join(', '))

  // Update input values when config changes
  useEffect(() => {
    setBlockedDomainsInput(config.emailDomains.blocked.join(', '))
  }, [config.emailDomains.blocked])

  const handleChange = (field: keyof RegistrationConfig, value: any) => {
    onChange({
      ...config,
      [field]: value,
    })
  }

  const handleNestedChange = (parentField: keyof RegistrationConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      ...config,
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  // Custom handler for domain input to better handle comma-separated values
  const handleBlockedDomainsChange = (value: string) => {
    setBlockedDomainsInput(value)
  }

  const handleBlockedDomainsBlur = () => {
    // Process domains when user finishes typing
    const domains = blockedDomainsInput
      .split(',')
      .map(d => d.trim())
      .filter(d => d && d.length > 0)
    
    if (JSON.stringify(domains) !== JSON.stringify(config.emailDomains.blocked)) {
      handleNestedChange('emailDomains', 'blocked', domains)
    }
  }

  const handleBlockedDomainsKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleBlockedDomainsBlur()
    }
  }

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      {/* Registration Mode */}
      <ConfigSection
        title="Registration Mode"
        description="Configure how users can register for your platform"
        icon={<PersonAddIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Registration Mode"
              value={config.mode}
              onChange={(value) => handleChange('mode', value)}
              options={[
                { value: 'open', label: 'Open Registration' },
                { value: 'closed', label: 'Closed - invites only' },
              ]}
              disabled={disabled}
              helpText="Closed - invites only: only invited people and people whose email domain belongs to an existing company can join. Nobody can start a new company."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigField
              label="Waitlist URL"
              value={config.waitlistUrl ?? ''}
              onChange={(value) => handleChange('waitlistUrl', value)}
              type="url"
              placeholder={DEFAULT_SIGNUP_WAITLIST_URL}
              disabled={disabled}
              validation={waitlistUrlInputError}
              helpText={`While Closed, Create Account and the sign-up closed messages link here (opens in a new tab). Leave empty for ${DEFAULT_SIGNUP_WAITLIST_URL}.`}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Max Users"
              value={config.limits.maxUsers.toString()}
              onChange={(value) => handleNestedChange('limits', 'maxUsers', parseInt(value) || 1000)}
              type="number"
              placeholder="1000"
              disabled={disabled}
              helpText="Maximum number of users allowed on the platform"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Max Rooms"
              value={config.limits.maxRooms.toString()}
              onChange={(value) => handleNestedChange('limits', 'maxRooms', parseInt(value) || 100)}
              type="number"
              placeholder="100"
              disabled={disabled}
              helpText="Maximum number of rooms a user can create"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Max Storage (MB)"
              value={config.limits.maxStorage.toString()}
              onChange={(value) => handleNestedChange('limits', 'maxStorage', parseInt(value) || 1000)}
              type="number"
              placeholder="1000"
              disabled={disabled}
              helpText="Maximum storage per user in megabytes"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Email Domain Management */}
      <ConfigSection
        title="Email Domain Management"
        description="Manage email domain restrictions and company assignments"
        icon={<SecurityIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Require Email Verification"
              value={config.emailDomains.requireVerification}
              onChange={(value) => handleNestedChange('emailDomains', 'requireVerification', value)}
              description="Require users to verify their email address before accessing the platform"
              disabled={disabled}
            />
          </Grid>
          

          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Blocked Domains"
              value={blockedDomainsInput}
              onChange={(value) => handleBlockedDomainsChange(value)}
              onBlur={handleBlockedDomainsBlur}
              onKeyDown={handleBlockedDomainsKeyDown}
              type="text"
              placeholder="spam.com, fake.org"
              disabled={disabled}
              helpText={
                blockedDomainsShown ? BLOCKED_DOMAINS_HELP : BLOCKED_DOMAINS_NOT_SHOWN_HELP
              }
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Required Fields */}
      <ConfigSection
        title="Required Fields"
        description="Configure which fields are required during registration"
        icon={<PersonAddIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="First Name Required"
              value={config.requiredFields.firstName}
              onChange={(value) => handleNestedChange('requiredFields', 'firstName', value)}
              description="Require users to provide their first name"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Last Name Required"
              value={config.requiredFields.lastName}
              onChange={(value) => handleNestedChange('requiredFields', 'lastName', value)}
              description="Require users to provide their last name"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Email Required"
              value={config.requiredFields.email}
              onChange={(value) => handleNestedChange('requiredFields', 'email', value)}
              description="Require users to provide their email address"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Phone Required"
              value={config.requiredFields.phone}
              onChange={(value) => handleNestedChange('requiredFields', 'phone', value)}
              description="Require users to provide their phone number"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Company Required"
              value={config.requiredFields.company}
              onChange={(value) => handleNestedChange('requiredFields', 'company', value)}
              description="Require users to provide their company name"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Job Title Required"
              value={config.requiredFields.title}
              onChange={(value) => handleNestedChange('requiredFields', 'title', value)}
              description="Require users to provide their job title"
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Verification Settings */}
      <ConfigSection
        title="Verification Settings"
        description="Configure verification and approval processes"
        icon={<SecurityIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Email Verification"
              value={config.verification.emailVerification}
              onChange={(value) => handleNestedChange('verification', 'emailVerification', value)}
              description="Require users to verify their email address"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Phone Verification"
              value={config.verification.phoneVerification}
              onChange={(value) => handleNestedChange('verification', 'phoneVerification', value)}
              description="Require users to verify their phone number"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Admin Approval"
              value={config.verification.adminApproval}
              onChange={(value) => handleNestedChange('verification', 'adminApproval', value)}
              description="Require admin approval for new user registrations"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Auto Approve"
              value={config.verification.autoApprove}
              onChange={(value) => handleNestedChange('verification', 'autoApprove', value)}
              description="Automatically approve new user registrations"
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Terms and Conditions */}
      <ConfigSection
        title="Terms and Conditions"
        description="Configure terms of service and legal requirements"
        icon={<GavelIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Terms of Service"
              value={config.terms.enabled}
              onChange={(value) => handleNestedChange('terms', 'enabled', value)}
              description="Show terms of service during registration"
              disabled={disabled}
            />
          </Grid>
          
          {config.terms.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Terms URL"
                  value={config.terms.url || ''}
                  onChange={(value) => handleNestedChange('terms', 'url', value)}
                  type="url"
                  placeholder="https://yourplatform.com/terms"
                  disabled={disabled}
                  helpText="URL to your terms of service document"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigToggle
                  label="Require Acceptance"
                  value={config.terms.requireAcceptance}
                  onChange={(value) => handleNestedChange('terms', 'requireAcceptance', value)}
                  description="Require users to accept terms before registration"
                  disabled={disabled}
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>
    </Box>
  )
}

export default RegistrationSettings 